// pages/reports/TaxReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
    ChevronUp, 
    RotateCcw, 
    Download,
    Printer,
    
    TrendingUp,
    TrendingDown,
    PieChart,
    FileText,
    Calendar,
    Percent,
    ShoppingBag,
    Truck,
    RefreshCw,
    BarChart2,
   
} from 'feather-icons-react/build/IconComponents';
import { reportActions } from '../../core/action/reportActions';

const MySwal = withReactContent(Swal);

const TaxReport = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    
    // State
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState('summary');
    const [taxType, setTaxType] = useState('all');
    
    // Format date for API
    const formatDateForAPI = (date) => {
        return date.toISOString().split('T')[0];
    };
    
    // Load report data
    const loadReport = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.getTaxReport(params);
            
            if (result.success) {
                setReportData(result.data);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load report',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading report:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load report',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate]);
    
    // Load on mount and when dependencies change
    useEffect(() => {
        loadReport();
    }, [loadReport]);
    
    // Handle export PDF
    const handleExportPDF = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.exportToPDF('tax', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `tax_report_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export PDF',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle export Excel
    const handleExportExcel = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.exportToExcel('tax', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `tax_report_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting Excel:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export Excel',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle print
    const handlePrint = () => {
        window.print();
    };
    
    // Filter tax rates
    const getFilteredSalesRates = () => {
        if (!reportData?.rate_breakdown_sales) return [];
        if (taxType === 'all') return reportData.rate_breakdown_sales;
        return reportData.rate_breakdown_sales.filter(r => 
            r.tax_name.toLowerCase().includes(taxType.toLowerCase())
        );
    };
    
    const getFilteredPurchaseRates = () => {
        if (!reportData?.rate_breakdown_purchases) return [];
        if (taxType === 'all') return reportData.rate_breakdown_purchases;
        return reportData.rate_breakdown_purchases.filter(r => 
            r.tax_name.toLowerCase().includes(taxType.toLowerCase())
        );
    };
    
    // Render filter bar
    const renderFilterBar = () => {
        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={setFromDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={toDate}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={setToDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                minDate={fromDate}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Tax Type</label>
                            <Select
                                options={[
                                    { value: 'all', label: 'All Taxes' },
                                    { value: 'cgst', label: 'CGST' },
                                    { value: 'sgst', label: 'SGST' },
                                    { value: 'igst', label: 'IGST' }
                                ]}
                                value={{ value: taxType, label: taxType === 'all' ? 'All Taxes' : taxType.toUpperCase() }}
                                onChange={(opt) => setTaxType(opt?.value || 'all')}
                                placeholder="Select tax type"
                            />
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                            <button className="btn btn-primary w-100" onClick={loadReport}>
                                <RefreshCw size={16} className="me-2" />
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render summary cards
    const renderSummaryCards = () => {
        if (!reportData) return null;
        
        const { summary } = reportData;
        
        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Output Tax (Sales)</p>
                                    <h3 className="mb-0 text-danger">
                                        {reportActions.formatCurrency(summary.sales.total_tax)}
                                    </h3>
                                    <small className="text-muted">{summary.sales.invoice_count} invoices</small>
                                </div>
                                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                                    <TrendingUp size={24} className="text-danger" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Input Tax (Purchases)</p>
                                    <h3 className="mb-0 text-success">
                                        {reportActions.formatCurrency(summary.purchases.total_tax)}
                                    </h3>
                                    <small className="text-muted">{summary.purchases.purchase_count} purchases</small>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <TrendingDown size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Net Tax Payable</p>
                                    <h3 className="mb-0 text-primary">
                                        {reportActions.formatCurrency(summary.net_tax_payable)}
                                    </h3>
                                    <small className="text-muted">Output - Input</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <Percent size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Effective Tax Rate</p>
                                    <h3 className="mb-0 text-info">
                                        {summary.sales.effective_rate}%
                                    </h3>
                                    <small className="text-muted">On sales</small>
                                </div>
                                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                                    <BarChart2 size={24} className="text-info" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render tabs
    const renderTabs = () => {
        const tabs = [
            { id: 'summary', label: 'Summary', icon: <PieChart size={16} /> },
            { id: 'sales', label: 'Sales Tax', icon: <ShoppingBag size={16} /> },
            { id: 'purchases', label: 'Purchase Tax', icon: <Truck size={16} /> },
            { id: 'monthly', label: 'Monthly Breakdown', icon: <Calendar size={16} /> }
        ];
        
        return (
            <ul className="nav nav-tabs mb-4">
                {tabs.map(tab => (
                    <li className="nav-item" key={tab.id}>
                        <button
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span className="ms-2">{tab.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
        );
    };
    
    // Render summary tab
    const renderSummaryTab = () => {
        if (!reportData) return null;
        
        const { summary } = reportData;
        
        return (
            <div className="row">
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header bg-danger bg-opacity-10">
                            <h6 className="mb-0 text-danger">Sales Tax Summary</h6>
                        </div>
                        <div className="card-body">
                            <table className="table table-sm">
                                <tbody>
                                    <tr>
                                        <td>Total Invoices</td>
                                        <td className="text-end"><strong>{summary.sales.invoice_count}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Taxable Amount</td>
                                        <td className="text-end">{reportActions.formatCurrency(summary.sales.total_taxable)}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Tax</td>
                                        <td className="text-end text-danger">{reportActions.formatCurrency(summary.sales.total_tax)}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Amount (incl. tax)</td>
                                        <td className="text-end">{reportActions.formatCurrency(summary.sales.total_amount)}</td>
                                    </tr>
                                    <tr>
                                        <td>Effective Tax Rate</td>
                                        <td className="text-end"><strong>{summary.sales.effective_rate}%</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header bg-success bg-opacity-10">
                            <h6 className="mb-0 text-success">Purchase Tax Summary</h6>
                        </div>
                        <div className="card-body">
                            <table className="table table-sm">
                                <tbody>
                                    <tr>
                                        <td>Total Purchases</td>
                                        <td className="text-end"><strong>{summary.purchases.purchase_count}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Taxable Amount</td>
                                        <td className="text-end">{reportActions.formatCurrency(summary.purchases.total_taxable)}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Tax</td>
                                        <td className="text-end text-success">{reportActions.formatCurrency(summary.purchases.total_tax)}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Amount (incl. tax)</td>
                                        <td className="text-end">{reportActions.formatCurrency(summary.purchases.total_amount)}</td>
                                    </tr>
                                    <tr>
                                        <td>Effective Tax Rate</td>
                                        <td className="text-end"><strong>{summary.purchases.effective_rate}%</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                
                <div className="col-md-12">
                    <div className="card">
                        <div className="card-header bg-primary bg-opacity-10">
                            <h6 className="mb-0 text-primary">Tax Liability</h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-4 text-center">
                                    <p className="text-muted mb-1">Output Tax (Sales)</p>
                                    <h3 className="text-danger">{reportActions.formatCurrency(summary.sales.total_tax)}</h3>
                                </div>
                                <div className="col-md-4 text-center">
                                    <p className="text-muted mb-1">Input Tax (Purchases)</p>
                                    <h3 className="text-success">{reportActions.formatCurrency(summary.purchases.total_tax)}</h3>
                                </div>
                                <div className="col-md-4 text-center">
                                    <p className="text-muted mb-1">Net Payable</p>
                                    <h3 className={summary.net_tax_payable >= 0 ? 'text-danger' : 'text-success'}>
                                        {reportActions.formatCurrency(summary.net_tax_payable)}
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render sales tax tab
    const renderSalesTaxTab = () => {
        const filteredRates = getFilteredSalesRates();
        
        if (filteredRates.length === 0) {
            return (
                <div className="alert alert-info">
                    No sales tax data available for the selected period.
                </div>
            );
        }
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Tax Rate Breakdown (Sales)</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Tax Name</th>
                                    <th>Tax Code</th>
                                    <th className="text-end">Rate (%)</th>
                                    <th className="text-end">Line Items</th>
                                    <th className="text-end">Taxable Amount</th>
                                    <th className="text-end">Tax Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRates.map((rate, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{rate.tax_name}</strong>
                                        </td>
                                        <td>{rate.tax_code}</td>
                                        <td className="text-end">{rate.tax_rate}%</td>
                                        <td className="text-end">{rate.line_items}</td>
                                        <td className="text-end">{reportActions.formatCurrency(rate.taxable_amount)}</td>
                                        <td className="text-end text-danger">{reportActions.formatCurrency(rate.total_tax)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <th colSpan="4" className="text-end">Total</th>
                                    <th className="text-end">
                                        {reportActions.formatCurrency(
                                            filteredRates.reduce((sum, r) => sum + r.taxable_amount, 0)
                                        )}
                                    </th>
                                    <th className="text-end text-danger">
                                        {reportActions.formatCurrency(
                                            filteredRates.reduce((sum, r) => sum + r.total_tax, 0)
                                        )}
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render purchases tax tab
    const renderPurchasesTaxTab = () => {
        const filteredRates = getFilteredPurchaseRates();
        
        if (filteredRates.length === 0) {
            return (
                <div className="alert alert-info">
                    No purchase tax data available for the selected period.
                </div>
            );
        }
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Tax Rate Breakdown (Purchases)</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Tax Name</th>
                                    <th>Tax Code</th>
                                    <th className="text-end">Rate (%)</th>
                                    <th className="text-end">Line Items</th>
                                    <th className="text-end">Taxable Amount</th>
                                    <th className="text-end">Tax Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRates.map((rate, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{rate.tax_name}</strong>
                                        </td>
                                        <td>{rate.tax_code}</td>
                                        <td className="text-end">{rate.tax_rate}%</td>
                                        <td className="text-end">{rate.line_items}</td>
                                        <td className="text-end">{reportActions.formatCurrency(rate.taxable_amount)}</td>
                                        <td className="text-end text-success">{reportActions.formatCurrency(rate.total_tax)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <th colSpan="4" className="text-end">Total</th>
                                    <th className="text-end">
                                        {reportActions.formatCurrency(
                                            filteredRates.reduce((sum, r) => sum + r.taxable_amount, 0)
                                        )}
                                    </th>
                                    <th className="text-end text-success">
                                        {reportActions.formatCurrency(
                                            filteredRates.reduce((sum, r) => sum + r.total_tax, 0)
                                        )}
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render monthly tab
    const renderMonthlyTab = () => {
        if (!reportData?.monthly_breakdown || reportData.monthly_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No monthly tax data available.
                </div>
            );
        }
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Monthly Tax Breakdown</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Month</th>
                                    <th className="text-end">Sales Count</th>
                                    <th className="text-end">Sales Tax</th>
                                    <th className="text-end">Purchase Count</th>
                                    <th className="text-end">Purchase Tax</th>
                                    <th className="text-end">Net Tax</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.monthly_breakdown.map((month, idx) => {
                                    // const netTax = month.type === 'sales' 
                                    //     ? month.total_tax 
                                    //     : -month.total_tax;
                                    return (
                                        <tr key={idx}>
                                            <td>
                                                <strong>{month.month}</strong>
                                            </td>
                                            <td className="text-end">
                                                {month.type === 'sales' ? month.invoice_count : '-'}
                                            </td>
                                            <td className="text-end text-danger">
                                                {month.type === 'sales' ? reportActions.formatCurrency(month.total_tax) : '-'}
                                            </td>
                                            <td className="text-end">
                                                {month.type === 'purchase' ? month.invoice_count : '-'}
                                            </td>
                                            <td className="text-end text-success">
                                                {month.type === 'purchase' ? reportActions.formatCurrency(month.total_tax) : '-'}
                                            </td>
                                            <td className="text-end fw-bold">
                                                {reportActions.formatCurrency(month.type === 'sales' ? month.total_tax : -month.total_tax)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);
    
    if (loading && !reportData) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Tax Report...</h5>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Tax Report</h4>
                            <h6>Track tax liability and compliance</h6>
                        </div>
                    </div>
                    
                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <button className="btn btn-outline-danger" onClick={handleExportPDF}>
                                    <Download size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <button className="btn btn-outline-success" onClick={handleExportExcel}>
                                    <FileText size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <Printer size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={loadReport}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>
                
                {/* Period Info */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="card bg-light">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Period:</strong> {reportActions.formatDate(reportData?.period.from_date)} to {reportActions.formatDate(reportData?.period.to_date)}
                                    </div>
                                    <div>
                                        <strong>Net Tax Payable:</strong> 
                                        <span className={reportData?.summary.net_tax_payable >= 0 ? 'text-danger ms-2' : 'text-success ms-2'}>
                                            {reportActions.formatCurrency(reportData?.summary.net_tax_payable || 0)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Filter Bar */}
                {renderFilterBar()}
                
                {/* Summary Cards */}
                {renderSummaryCards()}
                
                {/* Tabs */}
                {renderTabs()}
                
                {/* Tab Content */}
                {activeTab === 'summary' && renderSummaryTab()}
                {activeTab === 'sales' && renderSalesTaxTab()}
                {activeTab === 'purchases' && renderPurchasesTaxTab()}
                {activeTab === 'monthly' && renderMonthlyTab()}
            </div>
        </div>
    );
};

export default TaxReport;